import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { colors } from '../../themes/typography';
import { experimentColors } from '../../themes/experimentColors';
import DiscountInput from './DiscountInput';

/**
 * Price discount section component for promo_precio_unitario experiments
 * Shows suggested discount for a single product with pricing display
 */
const PriceSection = ({
  item,
  productKey,
  isEditable,
  selectedCombo,
  experiment,
  getEffectiveDiscount,
  calculateDiscountedPrice,
  apiResponseOverrides,
  customDiscounts,
  discountInputs,
  setDiscountInputs,
  pendingSaves,
  updatedStates,
  onDiscountInputChange,
  onDiscountReset,
  onSaveChanges,
  t,
  productName // Product name from adapter
}) => {
  // A price increase moves the price up and has no candidates, so it renders a read-only
  // price pair instead of the editable discount row.
  const isPriceIncrease = experiment?.experimentType === 'alza_precio_sugerido';
  const markup = Number(experiment?.markup) || 0;
  const basePrice = Number(experiment?.originalPrice) || 0;
  const increasedPrice = Math.round(basePrice * (1 + markup / 100) * 100) / 100;

  return (
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px', justifyContent: 'space-between' }}>
        <Typography sx={{
          fontSize: '9px',
          color: colors.text.primary,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          lineHeight: 1
        }}>
          {isPriceIncrease ? t('suggestedPriceIncrease') : t('suggestedDiscount')}
        </Typography>
      </Box>
      
      {/* Product Name (no candidate selection for price experiments) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '6px' }}>
        <Tooltip title={productName} arrow placement="top">
          <Typography sx={{
            fontSize: '10px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1.2,
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {productName}
          </Typography>
        </Tooltip>
      </Box>

      {/* Price increase: read-only new/old price pair. No candidates, nothing to edit. */}
      {isPriceIncrease && basePrice > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '12px', color: colors.text.primary, fontWeight: 400, lineHeight: 1.2 }}>
            €{increasedPrice.toFixed(2)}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colors.text.secondary, fontWeight: 500, lineHeight: 1.2 }}>
            /
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: colors.text.secondary,
            fontWeight: 500,
            lineHeight: 1.2,
            textDecoration: 'line-through'
          }}>
            €{basePrice.toFixed(2)}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: colors.text.secondary, fontWeight: 500, lineHeight: 1.2 }}>
            +{markup}%
          </Typography>
        </Box>
      )}

      {/* Show pricing for price experiments */}
      {!isPriceIncrease && experiment?.comboPrices && experiment.comboPrices.length > 0 && (() => {
        const comboData = experiment.comboPrices[0]; // Always use first (and only) element for price experiments
        const originalPrice = comboData.totalWithoutDiscount || comboData.originalPrice || 0;
        const effectiveDiscount = getEffectiveDiscount(productKey, item, selectedCombo);
        
        // Check for API response overrides first
        const overrideKey = `${productKey}-${productName}`;
        let effectiveDiscountedPrice;
        
        if (apiResponseOverrides[overrideKey]) {
          effectiveDiscountedPrice = apiResponseOverrides[overrideKey].price || 0;
        } else if (customDiscounts[productKey] !== undefined) {
          effectiveDiscountedPrice = calculateDiscountedPrice(originalPrice, effectiveDiscount);  // User modified
        } else {
          effectiveDiscountedPrice = comboData.totalWithDiscount || 0;  // Use API value
        }
        
        // Ensure we have a valid number
        effectiveDiscountedPrice = effectiveDiscountedPrice || 0;
          
        // Initialize discount input if not set
        if (discountInputs[productKey] === undefined) {
          setDiscountInputs(prev => ({ ...prev, [productKey]: effectiveDiscount.toString() }));
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px', flexWrap: 'wrap' }}>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.primary,
              fontWeight: 400,
              lineHeight: 1.2
            }}>
              €{(effectiveDiscountedPrice || 0).toFixed(2)}
            </Typography>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.secondary,
              fontWeight: 500,
              lineHeight: 1.2
            }}>
              /
            </Typography>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.secondary,
              fontWeight: 500,
              lineHeight: 1.2,
              textDecoration: 'line-through'
            }}>
              €{(originalPrice || 0).toFixed(2)}
            </Typography>
            <Typography sx={{
              fontSize: '10px',
              color: colors.text.secondary,
              fontWeight: 500,
              lineHeight: 1.2
            }}>
              -
            </Typography>
            <DiscountInput
              productKey={productKey}
              isEditable={isEditable}
              discountInputs={discountInputs}
              effectiveDiscount={effectiveDiscount}
              pendingSaves={pendingSaves}
              onDiscountInputChange={onDiscountInputChange}
              onDiscountReset={onDiscountReset}
              onSaveChanges={onSaveChanges}
              t={t}
            />
          </Box>
        );
      })()}
      
      {updatedStates[productKey] && (
        <Typography sx={{
          fontSize: '9px',
          color: experimentColors.running,
          fontWeight: 600,
          mt: '2px',
          backgroundColor: `${experimentColors.running}15`,
          px: '4px',
          py: '1px',
          borderRadius: '4px'
        }}>
          ✓ Updated
        </Typography>
      )}
    </Box>
  );
};

export default PriceSection;
